import { useState, useEffect } from 'react';
import { X, Save, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { updateArticle, getKnowledgeCategories } from '../../api/knowledge';

const EditArticleModal = ({ isOpen, onClose, onSuccess, article }) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    category_id: '',
    content: ''
  });

  useEffect(() => {
    if (isOpen && article) {
      setFormData({
        title: article.title || '',
        category_id: article.category_id != null ? String(article.category_id) : '',
        content: article.content || ''
      });
    }
  }, [isOpen, article]);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      const res = await getKnowledgeCategories();
      if (res.success) {
        setCategories(res.data || []);
      }
    } catch (err) {
      console.error('Failed to load categories', err);
      toast.error('Could not load categories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!article?.id) return;
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }
    if (!formData.category_id || String(formData.category_id).trim() === '') {
      toast.error('Please select a category');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        category_id: formData.category_id ? parseInt(formData.category_id, 10) : null
      };
      const res = await updateArticle(article.id, payload);

      if (res.success) {
        toast.success('Article updated successfully!');
        onSuccess && onSuccess();
        onClose();
      } else {
        toast.error(res.message || 'Failed to update article');
      }
    } catch (error) {
      console.error('Update article error:', error);
      const msg = error.response?.data?.message || error.message || 'An error occurred while updating the article';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    if (!article?.id) return;
    try {
      setLoading(true);
      const res = await updateArticle(article.id, { is_published: true });
      if (res.success) {
        toast.success('Article published!');
        onSuccess && onSuccess();
        onClose();
      } else {
        toast.error(res.message || 'Failed to publish');
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to publish';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-800 flex flex-col max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            Edit Article
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Article Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="e.g. How to configure email settings"
                className="w-full px-4 py-3 bg-white dark:bg-slate-950 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Category <span className="text-red-500">*</span></label>
              <select
                className="w-full px-4 py-3 bg-white dark:bg-slate-950 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all appearance-none cursor-pointer text-gray-900 dark:text-white"
                value={formData.category_id}
                onChange={e => setFormData({ ...formData, category_id: e.target.value })}
              >
                <option value="">Select a category...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Content <span className="text-red-500">*</span></label>
              <textarea
                rows={12}
                placeholder="Write your article content here..."
                className="w-full px-4 py-3 bg-white dark:bg-slate-950 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all resize-none font-mono text-sm leading-relaxed text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                value={formData.content}
                onChange={e => setFormData({ ...formData, content: e.target.value })}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <FileText className="w-3 h-3" />
                Markdown formatting supported
              </p>
            </div>
          </div>

          <div className="p-5 border-t border-gray-100 dark:border-slate-800 flex justify-end gap-3 bg-gray-50 dark:bg-slate-900 shrink-0 flex-wrap">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-xl transition-all">
              Cancel
            </button>
            {article && !article.is_published && (
              <button
                type="button"
                onClick={handlePublish}
                disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Publish now
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold shadow-lg shadow-indigo-500/20 transition-all transform active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditArticleModal;
